// file: src/api/towingApi.js
// Header-based auth (Safari-safe). No cookies.

import {
  emitAuthExpired,
  clearAllAuth,
  hardRedirectToLogin,
} from "../auth/session";
import { getIdToken as fbGetIdToken } from "../lib/firebaseCompact";

export const API_BASE = "https://towing-portal-api.onrender.com";
export const TOWING_LOGIN_PATH = "/towing/login";

/* ---- UI-only storage (snapshot + remember flag) ---- */
const T_REMEMBER = "auth_remember";
const T_USER = "auth_user";

function storageAvailable(type) {
  try {
    const s = type === "local" ? window.localStorage : window.sessionStorage;
    const k = "__t__";
    s.setItem(k, "1");
    s.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

function getStore(remember) {
  // if remember=true prefer localStorage, else sessionStorage
  const preferLocal = !!remember;
  const localOk = storageAvailable("local");
  const sessionOk = storageAvailable("session");

  if (preferLocal && localOk) return window.localStorage;
  if (!preferLocal && sessionOk) return window.sessionStorage;

  // fallback order
  if (sessionOk) return window.sessionStorage;
  if (localOk) return window.localStorage;

  // no storage available (rare): return null
  return null;
}

export function setTowingAuth(payload = {}, remember = true) {
  const store = getStore(remember);
  if (!store) return;

  try {
    store.setItem(T_REMEMBER, String(!!remember));
    store.setItem(T_USER, JSON.stringify(payload.user || null));
  } catch {}

  // optional: also clear the other storage to avoid stale reads
  try {
    const other =
      store === window.localStorage
        ? window.sessionStorage
        : window.localStorage;
    other?.removeItem(T_REMEMBER);
    other?.removeItem(T_USER);
  } catch {}
}

export function clearTowingAuth() {
  try {
    window.localStorage.removeItem(T_REMEMBER);
    window.localStorage.removeItem(T_USER);
  } catch {}
  try {
    window.sessionStorage.removeItem(T_REMEMBER);
    window.sessionStorage.removeItem(T_USER);
  } catch {}
}

export function getTowingRemember() {
  // read from whichever store has it
  try {
    const v =
      window.localStorage.getItem(T_REMEMBER) ??
      window.sessionStorage.getItem(T_REMEMBER);
    return v === "true" || v === "1";
  } catch {
    return true;
  }
}

export function getTowingUser() {
  try {
    const raw =
      window.localStorage.getItem(T_USER) ??
      window.sessionStorage.getItem(T_USER);
    return JSON.parse(raw || "null");
  } catch {
    return null;
  }
}

/* ---- fetch helpers ---- */
async function fetchWithTimeout(resource, options = {}) {
  const { timeout = 0, signal: extSignal, ...rest } = options;
  const controller = new AbortController();
  const onAbort = () => controller.abort(extSignal?.reason);

  if (extSignal) {
    if (extSignal.aborted) onAbort();
    else extSignal.addEventListener("abort", onAbort);
  }
  let id = null;
  if (Number.isFinite(timeout) && timeout > 0) {
    id = setTimeout(
      () => controller.abort(new DOMException("timeout", "TimeoutError")),
      timeout,
    );
  }
  try {
    return await fetch(resource, { ...rest, signal: controller.signal });
  } finally {
    if (id) clearTimeout(id);
    if (extSignal) extSignal.removeEventListener("abort", onAbort);
  }
}
async function parseOrText(res) {
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) return res.text();
  try {
    return await res.json();
  } catch {
    return {};
  }
}

/* ---- login (optional bookkeeping; not used for auth later) ---- */
export async function towingLogin({ idToken, remember = true, signal } = {}) {
  if (!idToken) throw new Error("idToken is required");
  const res = await fetchWithTimeout(`${API_BASE}/api/v1/auth/login`, {
    method: "POST",
    // NOTE: no credentials, we don't rely on any cookie
    signal,
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ id_token: idToken, remember_me: !!remember }),
    timeout: 20000,
  });
  const body = await parseOrText(res);
  if (!res.ok) {
    const msg =
      (body && (body.message || body.error || body.detail)) ||
      `Login failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = body;
    throw err;
  }
  setTowingAuth(body, remember);
  return body;
}

/* ---- header-auth fetch + 1 retry with a fresh Firebase token ---- */
async function towingAuthFetch(url, options = {}) {
  const { autoRedirectOn401 = true, retryOn401 = true, ...fetchOpts } = options;

  const headers = new Headers(fetchOpts.headers || {});
  if (!headers.has("accept")) headers.set("accept", "application/json");

  // 1) try current ID token
  let idToken = "";
  try {
    idToken = await fbGetIdToken(false);
  } catch {}
  if (idToken) headers.set("authorization", `Bearer ${idToken}`);

  let res = await fetchWithTimeout(url, {
    ...fetchOpts,
    credentials: "omit", // 🚫 no cross-site cookies
    headers,
  });

  // 2) if 401/403, retry once with a *fresh* ID token
  if ((res.status === 401 || res.status === 403) && retryOn401) {
    try {
      const fresh = await fbGetIdToken(true);
      if (fresh && fresh !== idToken) {
        const h2 = new Headers(headers);
        h2.set("authorization", `Bearer ${fresh}`);
        res = await fetchWithTimeout(url, {
          ...fetchOpts,
          credentials: "omit",
          headers: h2,
        });
      }
    } catch {}
  }

  if (res.status !== 401 && res.status !== 403) return res;

  // 3) still unauthorized → expire + redirect
  emitAuthExpired("towing");
  if (autoRedirectOn401 !== false) {
    clearAllAuth();
    hardRedirectToLogin(TOWING_LOGIN_PATH);
  }
  return res;
}

/* ---- verbs ---- */
export async function apiGet(path, opts = {}) {
  const res = await towingAuthFetch(path, { method: "GET", ...opts });
  const body = await parseOrText(res);
  if (!res.ok) {
    const err = new Error(
      (body && (body.message || body.error || body.detail)) ||
        `Request failed (${res.status})`,
    );
    err.status = res.status;
    err.payload = body;
    throw err;
  }
  return body;
}
export async function apiPost(path, data, opts = {}) {
  const res = await towingAuthFetch(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(opts.headers || {}),
    },
    body: JSON.stringify(data),
    ...opts,
  });
  const body = await parseOrText(res);
  if (!res.ok) {
    const err = new Error(
      (body && (body.message || body.error || body.detail)) ||
        `Request failed (${res.status})`,
    );
    err.status = res.status;
    err.payload = body;
    throw err;
  }
  return body;
}

/* ---- endpoints ---- */
export async function listDoNotTow(
  { skip = 0, limit = 10000, active_only = false, search } = {},
  opts = {},
) {
  const qs = new URLSearchParams();
  if (Number.isFinite(skip)) qs.set("skip", String(skip));
  if (Number.isFinite(limit)) qs.set("limit", String(limit));
  if (typeof active_only === "boolean")
    qs.set("active_only", String(active_only));
  if (search) qs.set("search", String(search));

  // ✅ SMART DNT endpoint
  return apiGet(`${API_BASE}/api/v1/do-not-tow/smart?${qs.toString()}`, {
    signal: opts.signal,
    timeout: opts.timeout ?? 15000,
    cache: "no-store",
  });
}

export async function lookup(params = {}, opts = {}) {
  const search_type =
    params?.search_type === "title" ? "title" : "license_plate";

  // ---- TITLE SEARCH ----
  if (search_type === "title") {
    const title = String(params?.title || "").trim();
    if (!title) {
      const e = new Error("title is required for title search");
      e.status = 400;
      throw e;
    }

    const qs = new URLSearchParams({
      search_type: "title",
      title,
      ...(params?.date ? { date: params.date } : {}),
    });

    // ✅ ALWAYS call SMART endpoint
    return apiGet(`${API_BASE}/api/v1/lookup/plate/smart/_?${qs.toString()}`, {
      signal: opts.signal,
      timeout: 0,
      cache: "no-store",
    });
  }

  // ---- LICENSE PLATE SEARCH ----
  const plate = String(params?.plate_number || "").trim();
  if (!plate) {
    const e = new Error("plate_number is required");
    e.status = 400;
    throw e;
  }

  // ✅ ALWAYS call SMART endpoint
  return apiGet(
    `${API_BASE}/api/v1/lookup/plate/smart/${encodeURIComponent(plate)}`,
    { signal: opts.signal, timeout: 0, cache: "no-store" },
  );
}

export async function lookupPlate(plate_number, opts = {}) {
  const plate = String(plate_number || "").trim();
  if (!plate) {
    const e = new Error("plate_number is required");
    e.status = 400;
    throw e;
  }

  // ✅ Always use SMART endpoint
  return apiGet(
    `${API_BASE}/api/v1/lookup/plate/smart/${encodeURIComponent(plate)}`,
    { signal: opts.signal, timeout: 0, cache: "no-store" },
  );
}

// ✅ Title search dropdown API
export async function searchFacilityTitles({ q, limit = 10 } = {}, opts = {}) {
  const qq = String(q || "").trim();
  if (!qq) return { results: [] };

  const qs = new URLSearchParams();
  qs.set("q", qq);
  qs.set("limit", String(limit));

  // IMPORTANT: includes /lookup prefix
  return apiGet(
    `${API_BASE}/api/v1/lookup/facilities/search?${qs.toString()}`,
    {
      signal: opts.signal,
      timeout: opts.timeout ?? 15000,
      cache: "no-store",
    },
  );
}

export default {
  API_BASE,
  TOWING_LOGIN_PATH,
  towingLogin,
  setTowingAuth,
  clearTowingAuth,
  getTowingRemember,
  getTowingUser,
  listDoNotTow,
  lookup,
  lookupPlate,
  searchFacilityTitles,
  apiGet,
  apiPost,
};
